import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Play, Copy, Check, Linkedin, Twitter, MessageCircle, Facebook } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import PageSeo from "@/components/PageSeo";
import { HoverVideo } from "@/components/VimeoEmbed";
import { projectPoster, vimeoFullUrl } from "@/lib/vimeo";
import { divisionColor } from "@/lib/divisions";
import { SITE } from "@/constants/testIds";

/**
 * Netflix-style poster hero: static image, silent muted preview loop on hover
 * (desktop) or when scrolled into view (mobile). Click swaps to the full player
 * with sound. Teaser is destroyed once the full player mounts so we never
 * stream two Vimeo instances at once.
 */
function ProjectHero({ project, poster, playing, onPlay }) {
  const [hovered, setHovered] = useState(false);
  const [autoTease, setAutoTease] = useState(false);
  const ref = useRef(null);

  // Mobile: auto-play the teaser when the hero scrolls into view. Uses a
  // coarse-pointer check so desktop users are still driven by hover.
  useEffect(() => {
    if (playing) { setAutoTease(false); return; }
    const isTouch = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;
    if (!isTouch) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => setAutoTease(entries[0]?.isIntersecting === true),
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [playing]);

  const showTease = !playing && (hovered || autoTease);

  return (
    <div
      ref={ref}
      className="mt-8 relative w-full bg-charcoal overflow-hidden"
      style={{
        aspectRatio: project.aspect === "9:16" ? "9 / 16" : "16 / 9",
        maxHeight: project.aspect === "9:16" ? "90vh" : "72vh",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid="project-hero"
    >
      {/* Static poster (base layer) */}
      {!playing && (
        <img src={poster} alt={project.title} className="absolute inset-0 w-full h-full object-cover" data-testid="project-poster" />
      )}

      {/* Silent teaser loop — reuses the same HoverVideo component the Home
          and Work grids use so behaviour + performance profile is identical. */}
      {!playing && (
        <HoverVideo id={project.vimeo_id} active={showTease} overlay="bg-ink/25" />
      )}

      {/* Play button overlay */}
      {!playing && (
        <button
          type="button"
          onClick={onPlay}
          data-testid="project-play-btn"
          aria-label={`Watch ${project.title} with sound`}
          className="group absolute inset-0 z-10 w-full h-full flex items-center justify-center"
        >
          <span
            className={`relative flex items-center gap-4 px-8 py-5 bg-bone text-ink eyebrow text-sm transition-all duration-300 ${
              showTease ? "bg-relatable text-bone scale-105" : "group-hover:bg-relatable group-hover:text-bone"
            }`}
          >
            <Play className="w-4 h-4" fill="currentColor" /> Watch The Film · With Sound
          </span>
        </button>
      )}

      {playing && (
        <iframe
          title={project.title}
          src={vimeoFullUrl(project.vimeo_id)}
          allow="autoplay; fullscreen; picture-in-picture"
          className="absolute inset-0 w-full h-full"
          data-testid="project-player"
        />
      )}
    </div>
  );
}

/**
 * Pre-filled social-share strip with UTM tags for attribution. Shows OG
 * preview cards so the poster knows what LinkedIn/WhatsApp/X will render,
 * then one-click deep-links open each platform's share dialog with the
 * project URL + a suggested caption. Every share URL carries
 * `utm_source=<platform>&utm_medium=share` so the dashboard attributes
 * inbound leads to whichever channel actually drove the click.
 */
function ShareStrip({ project, description }) {
  const baseUrl = "https://relatable.studio";
  const canonicalUrl = `${baseUrl}/work/${project.slug}`;
  const poster = projectPoster(project);

  const buildUrl = (source) => `${canonicalUrl}?utm_source=${source}&utm_medium=share&utm_campaign=project-${project.slug}`;

  const trimmedDesc = (description || `A ${project.division || "Studio"} film by Relatable Studio.`).replace(/\s+/g, " ").trim().slice(0, 220);
  const caption = `${project.title} — a ${project.division || "Studio"} film by Relatable Studio.`;
  const twitterText = `${project.title} · Relatable Studio${project.division ? " · " + project.division : ""}`;

  const links = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(buildUrl("linkedin"))}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${caption} — ${buildUrl("whatsapp")}`)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(buildUrl("twitter"))}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(buildUrl("facebook"))}`,
  };

  const [copied, setCopied] = useState(false);
  const copyDirect = async () => {
    try {
      await navigator.clipboard.writeText(buildUrl("direct"));
      setCopied(true);
      toast.success("Attributed link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed. Select the URL manually.");
    }
  };

  const platformDomain = (url) => {
    try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return "relatable.studio"; }
  };

  const shareBtns = [
    { key: "linkedin", label: "LinkedIn", icon: Linkedin, href: links.linkedin, accent: "hover:bg-[#0A66C2]" },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: links.whatsapp, accent: "hover:bg-[#25D366]" },
    { key: "twitter", label: "X · Twitter", icon: Twitter, href: links.twitter, accent: "hover:bg-relatable" },
    { key: "facebook", label: "Facebook", icon: Facebook, href: links.facebook, accent: "hover:bg-[#1877F2]" },
  ];

  return (
    <section
      data-testid="project-share-strip"
      className="mt-20 md:mt-24 px-6 md:px-12 lg:px-24 pb-8"
    >
      <div className="border-t border-line pt-10 grid grid-cols-12 gap-x-6 md:gap-x-10 gap-y-10">
        {/* Left: preview stack */}
        <div className="col-span-12 md:col-span-6">
          <p className="eyebrow text-fog mb-2">◇ Share This Film</p>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight font-medium text-bone leading-[1.05]">
            Ready-to-post,<br/>attribution built in.
          </h2>
          <p className="mt-4 text-fog text-sm md:text-base leading-relaxed max-w-md">
            Every share carries a UTM tag. Inbound leads from LinkedIn appear as <span className="text-bone font-mono">linkedin</span> in the dashboard — instantly answering &ldquo;which channel actually converts&rdquo;.
          </p>

          {/* Live OG preview mimics */}
          <div className="mt-8 space-y-4">
            {/* LinkedIn / generic preview card */}
            <div data-testid="share-preview-linkedin" className="border border-line bg-charcoal max-w-md">
              <div className="relative w-full" style={{ aspectRatio: "1.91 / 1" }}>
                <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <p className="eyebrow text-[10px] text-fog">{platformDomain(canonicalUrl)}</p>
                <p className="mt-1 text-bone text-sm font-medium truncate">{project.title} · {project.division || "Selected Work"} · Relatable Studio</p>
                <p className="mt-1 text-fog text-xs leading-snug line-clamp-2">{trimmedDesc}</p>
              </div>
            </div>

            {/* WhatsApp / iMessage preview */}
            <div data-testid="share-preview-whatsapp" className="border border-line bg-charcoal max-w-md p-3 flex gap-3">
              <img src={poster} alt="" className="w-16 h-16 object-cover shrink-0" />
              <div className="min-w-0">
                <p className="text-bone text-sm font-medium truncate">{project.title}</p>
                <p className="text-fog text-xs leading-snug line-clamp-2">{trimmedDesc}</p>
                <p className="eyebrow text-[10px] text-fog mt-1">{platformDomain(canonicalUrl)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right: share buttons */}
        <div className="col-span-12 md:col-span-6">
          <p className="eyebrow text-fog mb-4">◇ Share To</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {shareBtns.map(({ key, label, icon: Icon, href, accent }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`share-btn-${key}`}
                className={`group flex items-center justify-between gap-3 border border-line bg-ink hover:border-transparent transition-colors px-5 py-4 ${accent}`}
              >
                <span className="flex items-center gap-3">
                  <Icon className="w-4 h-4 text-bone" />
                  <span className="eyebrow text-bone">{label}</span>
                </span>
                <ArrowUpRight className="w-4 h-4 text-fog group-hover:text-bone transition-colors" />
              </a>
            ))}

            <button
              type="button"
              onClick={copyDirect}
              data-testid="share-btn-copy"
              className="group flex items-center justify-between gap-3 border border-line bg-ink hover:border-bone transition-colors px-5 py-4"
            >
              <span className="flex items-center gap-3">
                {copied ? <Check className="w-4 h-4 text-relatable" /> : <Copy className="w-4 h-4 text-bone" />}
                <span className="eyebrow text-bone">{copied ? "Copied!" : "Copy Direct Link"}</span>
              </span>
              <ArrowUpRight className="w-4 h-4 text-fog group-hover:text-bone transition-colors" />
            </button>
          </div>

          <div className="mt-6 border border-line p-4 bg-charcoal">
            <p className="eyebrow text-[10px] text-fog mb-2">◇ Base URL (all shares append UTM)</p>
            <p className="font-mono text-xs text-bone break-all" data-testid="share-canonical-url">{canonicalUrl}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Per-project detail page at /work/:slug.
 *
 * Adds dedicated URL, canonical, and per-project VideoObject + CreativeWork
 * schema so Google can rank each film independently and social platforms can
 * generate proper OG previews when someone shares the URL.
 */
export default function ProjectDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    setData(null);
    setError(null);
    setPlaying(false);
    let cancelled = false;

    async function load() {
      try {
        const [projRes, allRes] = await Promise.all([
          api.get(`/projects/${slug}`),
          api.get(`/projects`),
        ]);
        if (cancelled) return;
        setData(projRes.data);
        const p = projRes.data?.project;
        if (p) {
          const others = (allRes.data || [])
            .filter((x) => x.slug !== p.slug)
            .filter((x) => (x.division || "Studio") === (p.division || "Studio"))
            .slice(0, 3);
          const fill = (allRes.data || [])
            .filter((x) => x.slug !== p.slug && !others.find((o) => o.slug === x.slug))
            .slice(0, 3 - others.length);
          setRelated([...others, ...fill]);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.status === 404 ? "not_found" : "error");
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slug]);

  const jsonLd = useMemo(() => {
    const p = data?.project;
    if (!p) return null;
    const cs = data?.case_study;
    const desc = cs?.challenge || p.description || `${p.title} — a ${p.division || "Studio"} film by Relatable Studio.`;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": ["CreativeWork", "VideoObject"],
          "@id": `https://relatable.studio/work/${p.slug}#video`,
          "name": p.title,
          "description": desc,
          "url": `https://relatable.studio/work/${p.slug}`,
          "thumbnailUrl": projectPoster(p),
          "contentUrl": `https://vimeo.com/${p.vimeo_id}`,
          "embedUrl": `https://player.vimeo.com/video/${p.vimeo_id}`,
          "uploadDate": (p.created_at || "").slice(0, 10) || undefined,
          "genre": p.division || "Studio",
          "inLanguage": "en",
          "isFamilyFriendly": true,
          "creator": { "@type": "Organization", "@id": "https://relatable.studio/#organization" },
          "publisher": { "@type": "Organization", "@id": "https://relatable.studio/#organization" },
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://relatable.studio/" },
            { "@type": "ListItem", "position": 2, "name": "Work", "item": "https://relatable.studio/work" },
            { "@type": "ListItem", "position": 3, "name": p.title, "item": `https://relatable.studio/work/${p.slug}` },
          ]
        }
      ]
    };
  }, [data]);

  if (error === "not_found") {
    return (
      <main className="bg-ink text-bone min-h-screen flex flex-col items-center justify-center px-6" data-testid="project-not-found">
        <p className="eyebrow text-fog">◇ 404 · Project not found</p>
        <h1 className="mt-4 font-display text-5xl md:text-6xl tracking-tighter">This film doesn&rsquo;t exist.</h1>
        <p className="mt-4 text-fog">The slug <span className="text-bone">{slug}</span> has no matching project.</p>
        <Link to="/work" className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-bone text-ink eyebrow hover:bg-relatable hover:text-bone transition-colors" data-testid="project-back-to-work">
          <ArrowLeft className="w-4 h-4" /> Back to Selected Work
        </Link>
      </main>
    );
  }

  if (error === "error") {
    return (
      <main className="bg-ink text-bone min-h-screen flex flex-col items-center justify-center px-6" data-testid="project-load-error">
        <p className="eyebrow text-fog">◇ Something broke mid-reel</p>
        <h1 className="mt-4 font-display text-5xl md:text-6xl tracking-tighter">Couldn&rsquo;t load this film.</h1>
        <p className="mt-4 text-fog">A temporary glitch on our end — try again in a moment.</p>
        <div className="mt-10 flex items-center gap-4">
          <button
            onClick={() => window.location.reload()}
            data-testid="project-retry"
            className="inline-flex items-center gap-2 px-6 py-3 bg-bone text-ink eyebrow hover:bg-relatable hover:text-bone transition-colors"
          >
            Retry
          </button>
          <Link to="/work" className="inline-flex items-center gap-2 px-6 py-3 border border-line text-bone eyebrow hover:border-bone transition-colors" data-testid="project-error-back-to-work">
            <ArrowLeft className="w-4 h-4" /> Back to Selected Work
          </Link>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="bg-ink text-bone min-h-screen flex items-center justify-center" data-testid="project-loading">
        <p className="eyebrow text-fog animate-pulse">◇ Loading film …</p>
      </main>
    );
  }

  const p = data.project;
  const cs = data.case_study;
  const poster = projectPoster(p);
  // Branded 1200x630 OG image (falls back to Vimeo poster if render fails).
  const ogImage = `https://relatable.studio/api/og/${p.slug}.png`;

  const metaFields = [
    ["Division", p.division || "Studio"],
    ["Category", p.category || "—"],
    ["Client", p.client || "—"],
    ["Year", p.year || "—"],
    ["Industry", p.industry || "—"],
  ];

  return (
    <main data-testid="project-detail-page" className="bg-ink text-bone min-h-screen">
      <PageSeo
        title={`${p.title} · ${p.division || "Selected Work"} · Relatable Studio`}
        description={
          cs?.challenge?.slice(0, 155) ||
          p.description?.slice(0, 155) ||
          `${p.title} — a ${p.division || "Studio"} film produced by Relatable Studio, Bangalore.`
        }
        path={`/work/${p.slug}`}
        image={ogImage}
        type="video.other"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Work", path: "/work" },
          { name: p.title, path: `/work/${p.slug}` },
        ]}
        jsonLd={jsonLd}
      />

      {/* Hero — click-to-play film, teaser loop on hover (desktop) or when
          scrolled into view (mobile, since touch has no hover). */}
      <section className="pt-24 md:pt-28 px-6 md:px-12 lg:px-24">
        <Link to="/work" className="inline-flex items-center gap-2 eyebrow text-fog hover:text-bone transition-colors" data-testid="project-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Selected Work
        </Link>

        <ProjectHero
          project={p}
          poster={poster}
          playing={playing}
          onPlay={() => setPlaying(true)}
        />
      </section>

      {/* Title + eyebrow */}
      <section className="mt-12 md:mt-16 px-6 md:px-12 lg:px-24">
        <p className="eyebrow" style={{ color: divisionColor(p.division) }}>
          {p.division ? `RELATABLE ${p.division.toUpperCase()}` : "Selected Work"} · {p.category || "Film"}
        </p>
        <h1 className="mt-5 font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.95] max-w-[18ch]" data-testid="project-title">
          {p.title}
        </h1>
        {p.description && (
          <p className="mt-8 max-w-2xl text-fog text-base md:text-lg leading-relaxed">{p.description}</p>
        )}
      </section>

      {/* Meta panel */}
      <section className="mt-16 px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-2 md:grid-cols-5 border-t border-b border-line divide-x divide-line">
          {metaFields.map(([k, v]) => (
            <div key={k} className="p-5">
              <p className="eyebrow text-xs text-fog">{k}</p>
              <p className="mt-2 text-bone text-base md:text-lg truncate">{v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Case study prose */}
      {cs && (
        <section className="mt-20 md:mt-24 px-6 md:px-12 lg:px-24 pb-8">
          <p className="eyebrow text-fog mb-6">◇ The Case Study</p>
          <div className="grid grid-cols-12 gap-x-6 md:gap-x-10 gap-y-14">
            {[
              ["Challenge", cs.challenge],
              ["Approach", cs.approach],
              ["Strategy", cs.strategy],
              ["Production", cs.production],
              ["Execution", cs.execution],
              ["Outcome", cs.outcome],
            ].filter(([, v]) => v && v.trim()).map(([label, body]) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-15%" }}
                transition={{ duration: 0.55 }}
                className="col-span-12 md:col-span-6 grid grid-cols-12 gap-4"
              >
                <p className="col-span-12 md:col-span-3 eyebrow text-fog pt-2">{label}</p>
                <p className="col-span-12 md:col-span-9 text-bone text-lg md:text-xl leading-relaxed whitespace-pre-wrap">
                  {body}
                </p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Share for teams — pre-filled UTM'd links to LinkedIn/WhatsApp/X/FB */}
      <ShareStrip project={p} description={cs?.challenge || p.description} />

      {/* Related work */}
      {related.length > 0 && (
        <section className="mt-20 md:mt-24 px-6 md:px-12 lg:px-24 pb-24">
          <div className="flex items-end justify-between mb-8 border-t border-line pt-10">
            <p className="eyebrow text-fog">◇ Related Films</p>
            <Link to="/work" className="eyebrow text-bone link-line inline-flex items-center gap-2" data-testid="project-see-all">
              See all work <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {related.map((r) => (
              <li key={r.id} className="group bg-charcoal overflow-hidden">
                <Link to={`/work/${r.slug}`} className="block" data-testid={`project-related-${r.slug}`}>
                  <div className="relative w-full" style={{ aspectRatio: r.aspect === "9:16" ? "9 / 16" : "16 / 9" }}>
                    <img src={projectPoster(r)} alt={r.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    <div className="absolute inset-0 bg-ink/20 group-hover:bg-ink/10 transition-colors" />
                  </div>
                  <div className="p-5 flex items-center justify-between border-t border-line">
                    <span className="font-display tracking-tight truncate pr-4 text-bone">{r.title}</span>
                    <span className="eyebrow shrink-0" style={{ color: divisionColor(r.division) }}>{r.division || "Studio"}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-24 px-6 md:px-12 lg:px-24 border-t border-line text-center" data-testid={SITE.contactSection || "project-cta"}>
        <p className="eyebrow text-fog">◇ Working on something similar?</p>
        <h2 className="mt-6 font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.95]">
          Start the conversation.
        </h2>
        <Link to="/contact" className="mt-10 inline-flex items-center gap-3 px-7 py-4 bg-bone text-ink eyebrow hover:bg-relatable hover:text-bone transition-colors" data-testid="project-cta-link">
          Send Us A Brief <ArrowUpRight className="w-4 h-4" />
        </Link>
      </section>
    </main>
  );
}
